import {
  Box,
  Button,
  Container,
  Stack,
  Text,
} from '@spaceship-fspl/components';
import { TrackingCategory } from '@spaceship-fspl/tracking';
import { PostsOrPages } from '@tryghost/content-api';
import {
  ArticleCard,
  BlogTitle,
  BlogVerticalMarginContainer,
  getBlogPageLayoutStaticProps,
  getMetadataPropsFromPrismicSlice,
  Metadata,
  MetadataProps,
  TiledArticleCards,
  withBlogPageLayout,
} from 'components';
import { getPosts } from 'data/ghost/api-cached';
import { usePosts } from 'data/ghost/hooks';
import { getLearnHomepage } from 'data/prismic';
import * as React from 'react';

const INITIAL_POST_COUNT = 6;

interface LearnHomepageProps {
  title: string;
  description: string;
  metadata?: MetadataProps;
  posts: PostsOrPages;
  featured: PostsOrPages;
}

const LearnPage: React.FC<React.PropsWithChildren<LearnHomepageProps>> = ({
  title,
  description,
  metadata,
  posts: initialPosts,
  featured: [featuredPost],
}) => {
  const {
    data: allPosts,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = usePosts({
    initialData: initialPosts,
    params: {
      limit: INITIAL_POST_COUNT,
      ignore: featuredPost?.slug,
    },
  });

  return (
    <BlogVerticalMarginContainer>
      {metadata && <Metadata {...metadata} />}
      <Container>
        <Stack spaceY={{ xs: 'xl', lg: 'xxl' }} alignX="center">
          <Stack spaceY="md" alignX="center">
            <BlogTitle>{title}</BlogTitle>

            {description && (
              <Text align="center" variant={1} color="neutral.080">
                {description}
              </Text>
            )}
          </Stack>

          <Stack spaceY="xl">
            {featuredPost && (
              <TrackingCategory category="Feature Article">
                <ArticleCard
                  variant="featured"
                  key={featuredPost.slug}
                  slug={featuredPost.slug}
                  image={featuredPost.feature_image}
                  tag={featuredPost.primary_tag}
                  title={featuredPost.title}
                  description={featuredPost.excerpt}
                  author={{
                    ...featuredPost.primary_author,
                    profileImage: featuredPost.primary_author?.profile_image,
                  }}
                  readingTime={featuredPost.reading_time}
                />
              </TrackingCategory>
            )}
            <TiledArticleCards>
              {allPosts?.pages.flatMap((posts) =>
                posts.map(
                  ({
                    title,
                    slug,
                    feature_image,
                    excerpt,
                    primary_author,
                    reading_time,
                    primary_tag,
                  }) => (
                    <ArticleCard
                      key={slug}
                      slug={slug}
                      title={title}
                      image={feature_image}
                      tag={primary_tag}
                      description={excerpt}
                      author={{
                        ...primary_author,
                        profileImage: primary_author?.profile_image,
                      }}
                      readingTime={reading_time}
                    />
                  ),
                ),
              )}
            </TiledArticleCards>
            {hasNextPage && (
              <Box display="flex" justifyContent="center">
                <Button
                  variant="tertiary"
                  size="lg"
                  isLoading={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                >
                  Load more
                </Button>
              </Box>
            )}
          </Stack>
        </Stack>
      </Container>
    </BlogVerticalMarginContainer>
  );
};

export const getStaticProps = getBlogPageLayoutStaticProps<LearnHomepageProps>(
  async () => {
    const document = await getLearnHomepage();
    let metadata: MetadataProps | undefined = undefined;

    document.body?.map((slice) => {
      switch (slice.__typename) {
        case 'Learn_homepageBodyMetadata': {
          metadata = getMetadataPropsFromPrismicSlice(slice);
          break;
        }
      }
    });
    const featured = await getPosts({
      limit: 1,
    });
    const posts = await getPosts({
      limit: INITIAL_POST_COUNT,
      ignore: featured[0]?.slug,
    });

    return {
      props: {
        title: document.title || 'Spaceship Learn',
        description: document.description ?? '',
        metadata,
        posts,
        featured,
      },
    };
  },
);

export default withBlogPageLayout()(LearnPage);
